import React from "react";
import { useState } from "react";
import { Card, CardBody, CardHeader } from "reactstrap";
import Loader from "react-loaders";
import Chart from "react-apexcharts";
import { useEffect } from "react";
import PropTypes from "prop-types";
import { api } from "../../../_services/api.service";
import exceptionHandler from "../../../_helpers/exceptionHandler";
import { AppHelper } from "../../../_helpers/appHelper";
import colors from "../../../_helpers/colors";

const fieldTypes = {
  ShortText: "ShortText",
  LongText: "LongText",
  List: "List",
  DropDown: "DropDown",
  Number: "Number",
  Currency: "Currency",
  Date: "Date",
  YesNo: "YesNo",
};

const FieldBarChart = ({ field }) => {
  let [isLoading, setIsLoading] = useState(false);
  let [chartData, setChartData] = useState([]);
  let [chartHeight, setChartHeight] = useState(300);
  let [summary, setSummary] = useState();
  let [barChartSeries, setBarChartSeries] = useState([{ data: [] }]);
  let [isExpanded, setIsExpanded] = useState(false);
  let [barChartOptions, setBarChartOptions] = useState({
    chart: {
      id: "bar",
    },
    dataLabels: {
      enabled: false,
    },
    plotOptions: {
      bar: {
        horizontal: true,
      },
    },
    noData: {
      text: "There's no data",
      align: "center",
      verticalAlign: "middle",
      offsetX: 0,
      offsetY: 0,
    },
    // title: {
    //   text: field.fieldLabel,
    //   style: { fontWeight: "bold" },
    // },
    xaxis: {
      categories: [],
      labels: {
        show: true,
        formatter: (value) => {
          if (
            typeof value === "number" &&
            field.fieldType === fieldTypes.Currency
          )
            return AppHelper.formatMoney(value);

          if (typeof value === "number") return Math.trunc(value);

          if (field.fieldType === fieldTypes.Date && value.toDateString)
            return value.toDateString();

          return value;
        },
      },
    },
    yaxis: {
      labels: {
        show: true,
        align: "left",
        minWidth: 100,
        style: {
          color: undefined,
          fontSize: "12px",
          fontFamily: "Helvetica, Arial, sans-serif",
          cssClass: "apexcharts-yaxis-label",
        },
        offsetX: 0,
        offsetY: 0,
        rotate: 0,
        formatter: (value) => {
          if (typeof value === "number") return Math.trunc(value);
          return value;
        },
      },
    },
    tooltip: {
      enabled: true,
      y: {
        formatter: (val, otherData) => {
          return `${val}`;
        },
        title: {
          formatter: function(seriesName) {
            return `${seriesName}`;
          },
        },
      },
    },
    responsive: [
      {
        breakpoint: 600,
        options: {
          chart: {
            width: 350,
          },
          plotOptions: {
            bar: {
              horizontal: false,
            },
          },
          legend: {
            position: "bottom",
          },
          yaxis: {
            labels: {
              show: false,
            },
          },
        },
      },
    ],
  });

  useEffect(() => {
    loadAnswers();
  }, []);

  useEffect(() => {
    if (!chartData) return;

    let d = [...chartData];
    const groupedData = AppHelper.groupBy(d, "value");

    populateBarChart(groupedData);
    calculateSummary(d);
  }, [chartData]);

  const loadAnswers = async () => {
    setIsLoading(true);
    await api
      .getAnswersForField(field.fieldID)
      .then((data) => {
        if (data) {
          setChartData(data);
        }
      })
      .catch(exceptionHandler);
    setIsLoading(false);
  };

  const populateBarChart = (list) => {
    let options = { ...barChartOptions };
    let series = [];
    let data = { name: field.fieldLabel, data: [] };
    let keys = Object.keys(list);
    let height = 50 + 50 * keys.length;

    if (field.fieldType === fieldTypes.Number)
      keys = keys.map((x) => {
        return { answer: parseInt(x), count: list[x].length };
      });
    else if (field.fieldType === fieldTypes.Currency)
      keys = keys.map((x) => {
        return { answer: parseFloat(x), count: list[x].length };
      });
    else if (field.fieldType === fieldTypes.Date)
      keys = keys.map((x) => {
        return { answer: new Date(x), count: list[x].length };
      });
    else
      keys = keys.map((x) => {
        return { answer: x, count: list[x].length };
      });

    if (
      field.fieldType === fieldTypes.Number ||
      field.fieldType === fieldTypes.Currency ||
      field.fieldType === fieldTypes.Date
    ) {
      keys.sort((a, b) => {
        return a - b;
      });

      options.plotOptions.bar.horizontal = false;
    }

    keys.forEach((k) => {
      options.xaxis.categories.push(k.answer);
      data.data.push(k.count);
    });

    if (height > 100) setChartHeight(height);
    series.push(data);
    setBarChartOptions(options);
    setBarChartSeries(series);
  };

  // calculate lowest, highest and average for number and currency fields
  // calculate earliest and latest for date fields
  const calculateSummary = (data) => {
    let array;
    if (field.fieldType === fieldTypes.Number)
      array = data.map((x) => parseInt(x.value));
    else if (field.fieldType === fieldTypes.Currency)
      array = data.map((x) => parseFloat(x.value));
    else if (field.fieldType === fieldTypes.Date)
      array = data.map((x) => new Date(x.value));

    if (!array || array.length === 0) return;

    let min = Math.min.apply(Math, array),
      max = Math.max.apply(Math, array),
      avg;

    // format values
    if (field.fieldType === fieldTypes.Date) {
      if (min !== undefined) min = new Date(min).toDateString();
      if (max !== undefined) max = new Date(max).toDateString();
    }

    if (field.fieldType === fieldTypes.Currency) {
      if (min !== undefined) min = AppHelper.formatMoney(min);
      if (max !== undefined) max = AppHelper.formatMoney(max);

      avg = array.reduce((a, b) => a + b, 0) / array.length;
      avg = AppHelper.formatMoney(avg);
    }

    if (field.fieldType === fieldTypes.Number) {
      avg = array.reduce((a, b) => a + b, 0) / array.length;
    }

    setSummary({
      min: min,
      max: max,
      avg: avg,
    });
  };

  const parseSummary = () => {
    if (!summary) return "";

    let text = "";
    let lowestText = "Lowest";
    let highestText = "Highest";
    if (field.fieldType === fieldTypes.Date) {
      lowestText = "Earliest";
      highestText = "Latest";
    }

    if (summary.min !== undefined) text += `  ${lowestText}: ${summary.min}`;
    if (summary.max !== undefined) text += `  ${highestText}: ${summary.max}`;
    if (summary.avg !== undefined) text += `  Average: ${summary.avg}`;

    return text;
  };

  return (
    <Card className="main-card mb-3">
      <CardHeader>
        {field.fieldLabel}
        <div className="field-answer-summary">{parseSummary()}</div>
        <i
          className={isExpanded ? "fa fa-chevron-up" : "fa fa-chevron-down"}
          style={{
            fontSize: "25px",
            cursor: "pointer",
            marginLeft: "20px",
            color: colors.DODGER_BLUE,
          }}
          onClick={() => setIsExpanded(!isExpanded)}
        />
      </CardHeader>
      <br />
      {chartData && chartData.length > 0 && isExpanded && (
        <CardBody>
          {isLoading && <Loader style={{}} type="ball-pulse" active />}
          <Chart
            options={barChartOptions}
            series={barChartSeries}
            type="bar"
            height={`${chartHeight}px`}
          />
        </CardBody>
      )}
    </Card>
  );
};

FieldBarChart.propTypes = {
  field: PropTypes.shape({
    fieldID: PropTypes.number,
    fieldName: PropTypes.string,
    fieldType: PropTypes.oneOf([
      "ShortText",
      "LongText",
      "Number",
      "DropDown",
      "List",
      "Currency",
      "YesNo",
    ]),
    orderID: PropTypes.number,
    milestoneID: PropTypes.number,
    fieldLabel: PropTypes.string,
    instructions: PropTypes.string,
    mandatory: PropTypes.bool,
    points: PropTypes.number,
    addAltObj: PropTypes.oneOf(["NA", "Alternative", "Objective"]),
    addQuestion: PropTypes.string,
    altObjName: PropTypes.string,
    options: PropTypes.array,
  }),
  showModal: PropTypes.bool,
  toggleModal: PropTypes.func,
  reloadList: PropTypes.func,
};

FieldBarChart.defaultProps = {
  field: {
    fieldID: -1,
    fieldName: "",
    orderID: -1,
    milestoneID: 0,
    fieldType: "ShortText",
    fieldLabel: "",
    instructions: "",
    mandatory: false,
    points: 0,
    addAltObj: "NA",
    addQuestion: "",
    altObjName: "",
    options: [],
  },
  showModal: false,
};

export default FieldBarChart;
